'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseStaticHtmlContentOptions {
  enabled?: boolean;
}

interface UseStaticHtmlContentResult {
  htmlContent: string;
  isLoading: boolean;
  error: Error | null;
  fetchContent: () => Promise<void>;
}

export const useStaticHtmlContent = (
  path: string,
  { enabled = true }: UseStaticHtmlContentOptions = {},
): UseStaticHtmlContentResult => {
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetchContent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(path);

      if (!response.ok) {
        throw new Error(`Failed to fetch static html from ${path}`);
      }

      const html = await response.text();
      setHtmlContent(html);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load static html content'));
    } finally {
      setIsLoading(false);
    }
  }, [path]);

  const lastCalledPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (enabled && lastCalledPathRef.current !== path) {
      lastCalledPathRef.current = path;
      fetchContent();
    }
  }, [enabled, fetchContent, path]);

  return {
    htmlContent,
    isLoading,
    error,
    fetchContent,
  };
};
